/** Processing, To review - the bills that were read and wait for a person (ADR-0212).
 *
 * One row per document awaiting review, oldest first (DOC-06: the one that has waited longest is the
 * one worth finding). A row opens the review: the bill beside what was read from it, where the scope,
 * category, vendor and site are confirmed and the Accept is the person's act.
 *
 * **Scope and Category here are expected, not decided.** They come from the kind of document
 * (`expectedPlace`, ADR-0212 decision 3's table) so a list of fifty bills can be scanned before any
 * is opened; the classification that Accept would record is worked out at review from the bill, the
 * site's jurisdiction and the fuel, and nothing in this table is sent anywhere.
 *
 * **Emptiness is told apart from being done** (EXP-35): `GET /evidence/resume` says `nothing_uploaded`
 * and `everything_decided` differently, and the empty state reads that verdict, because rendering an
 * empty queue as success is the F07 shape in a different language.
 */

import { CheckCircle, ListMagnifyingGlass } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useDocuments, useIntake } from "../../api/evidence";
import type { DocumentRow } from "../../api/evidence";
import { useDocumentCounts } from "../../api/processing";
import { ReadFailed } from "../../components/Loaded";
import {
  EmptyAction,
  EmptyState,
  EMPTY_FILTERS,
  FilterLine,
  GridTable,
  InfoPopover,
  SkeletonRows,
  StatusChip,
} from "../../components/ui";
import type { FilterDef, FilterState, GridColumn } from "../../components/ui";
import { KIND_LABEL, kindLabel } from "../../lib/documentKinds";
import { expectedPlace } from "../../lib/expectedPlace";
import { shortDate } from "../../lib/format";
import { mayEnterData } from "../../shell/routeTable";
import { keepSearch } from "../search";
import { stageChipOf } from "../upload/stageChip";
import { useScreen } from "../useScreen";
import { MAY_UPLOAD } from "../upload/useSendFiles";
import { DocumentTiles, FileCell, ProcessingFrame } from "./common";
import styles from "./Processing.module.css";

const LIMIT = 200;

export function periodLabel(document: Pick<DocumentRow, "period_start" | "period_end">): string {
  return document.period_start && document.period_end
    ? `${shortDate(document.period_start)} – ${shortDate(document.period_end)}`
    : "not established";
}

/** `YYYY-MM` of a document's period start: the filter's option, and its test. */
const monthOf = (document: Pick<DocumentRow, "period_start">): string | null =>
  document.period_start ? document.period_start.slice(0, 7) : null;

export function ToReview() {
  const screen = useScreen();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const counts = useDocumentCounts();
  const read = useDocuments("awaiting_review", { oldestFirst: true, limit: LIMIT });
  //: The resume verdict is the Admin, Approver and Contributor's (`MAY_UPLOAD`); it is not asked on
  //: anybody else's behalf, because a 403 rendered as a failed read would say the product is broken.
  const mayAskIntake = me ? MAY_UPLOAD.includes(me.role) : false;
  const intake = useIntake(mayAskIntake);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const rows = read.data?.rows ?? [];
  const pick = (id: string) => filters.values[id]?.[0];
  const needle = filters.search.trim().toLowerCase();
  const shown = rows.filter(
    (row) =>
      (!pick("kind") || row.kind === pick("kind")) &&
      (!pick("site") || (row.facility_hint_name ?? "") === pick("site")) &&
      (!pick("period") || monthOf(row) === pick("period")) &&
      (!needle || (row.original_filename ?? "").toLowerCase().includes(needle)),
  );

  const sites = [...new Set(rows.flatMap((row) => row.facility_hint_name ?? []))].sort();
  const months = [...new Set(rows.flatMap((row) => monthOf(row) ?? []))].sort();
  const defs: FilterDef[] = [
    {
      id: "kind",
      label: "Type",
      mode: "single",
      allLabel: "Any type",
      options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
    },
    {
      id: "site",
      label: "Site",
      mode: "single",
      allLabel: "Any site",
      options: sites.map((name) => ({ value: name, label: name })),
    },
    {
      id: "period",
      label: "Period",
      mode: "single",
      allLabel: "Any period",
      options: months.map((month) => ({ value: month, label: month })),
    },
  ];

  const open = (document: DocumentRow) =>
    void navigate({
      to: "/evidence/$documentId",
      params: { documentId: document.id },
      search: { method: screen.method },
    });

  const columns: GridColumn<DocumentRow>[] = [
    { id: "file", header: "File", cell: (document) => <FileCell document={document} /> },
    { id: "type", header: "Type", cell: (document) => kindLabel(document.kind) },
    {
      id: "site",
      header: "Site (your hint)",
      cell: (document) => document.facility_hint_name ?? <span className={styles.none}>chosen in review</span>,
    },
    { id: "period", header: "Period", cell: periodLabel },
    {
      id: "scope",
      header: "Scope (expected)",
      cell: (document) => {
        const place = expectedPlace(document.kind);
        return place ? `Scope ${place.scope}` : <span className={styles.none}>–</span>;
      },
    },
    {
      id: "category",
      header: "Category (expected)",
      cell: (document) => expectedPlace(document.kind)?.category ?? <span className={styles.none}>–</span>,
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: (document) => document.vendor_name ?? <span className={styles.none}>suggested in review</span>,
    },
    {
      id: "stage",
      header: "Stage",
      cell: (document) => {
        const chip = stageChipOf(document);
        return (
          <StatusChip tone={chip.tone} size="sm">
            {chip.word}
          </StatusChip>
        );
      },
    },
    {
      id: "action",
      header: "Action",
      cell: (document) => (
        <Link
          className={styles.primary}
          to="/evidence/$documentId"
          params={{ documentId: document.id }}
          search={keepSearch}
          aria-label={`Review ${document.original_filename ?? "this document"}`}
        >
          Review
        </Link>
      ),
    },
  ];

  const verdict = intake.data?.verdict;
  //: A failed read of what is outstanding is never "nothing outstanding": the most confident sentence
  //: an empty queue can say, produced by a dropped request (EXP-29).
  const emptyState = (
    <EmptyState
      testId="to-review-empty"
      compact
      icon={verdict === "everything_decided" ? CheckCircle : ListMagnifyingGlass}
      title={
        verdict === "nothing_uploaded"
          ? "No bills yet"
          : verdict === "everything_decided"
            ? "Everything that arrived is decided"
            : "Nothing to review"
      }
      action={
        verdict === "nothing_uploaded" ? (
          <EmptyAction
            label="Upload a bill"
            allowed={me?.role === undefined ? undefined : mayEnterData(me.role)}
            reason="A Contributor, Approver or Admin uploads bills."
            to="/upload"
            search={keepSearch}
            plus={false}
            testId="to-review-empty-upload"
          />
        ) : undefined
      }
    >
      {verdict === "nothing_uploaded" ? "Bills you upload wait here for your review." : null}
    </EmptyState>
  );
  // Nothing has ever been uploaded: no filters and no charts, one empty state under the tiles. The
  // filter line carried the verdict (`resume-verdict`), so with no line the verdict stays on the empty
  // state as data (EXP-35: an empty library and a finished one are told apart by what the server said).
  const nothingYet = !read.error && !read.isPending && rows.length === 0 && verdict === "nothing_uploaded";
  // **The empty sentence waits for the verdict** (G-P9-09): "Nothing to review" is what the queue says when
  // the resume read answered something other than `nothing_uploaded` or `everything_decided`, and it was
  // drawn while that read was still out - the most confident sentence an empty queue can say, before
  // anybody had been asked. A role that is never asked (`MAY_UPLOAD`) has no verdict to wait for.
  const waitingForVerdict = me === undefined || (mayAskIntake && intake.isPending);
  const empty = intake.error ? (
    <ReadFailed of="What is outstanding" error={intake.error} onRetry={() => void intake.refetch()} />
  ) : waitingForVerdict ? (
    <SkeletonRows rows={3} />
  ) : nothingYet ? (
    <div
      data-testid="resume-verdict"
      data-verdict={verdict}
      data-outstanding={String(intake.data?.outstanding ?? "")}
    >
      {emptyState}
    </div>
  ) : (
    emptyState
  );

  return (
    <ProcessingFrame
      documentCharts
      nothingYet={nothingYet}
      tab="To review"
      tiles={
        <DocumentTiles
          counts={counts.data}
          pending={counts.isPending}
          failure={{ error: counts.error, retry: () => void counts.refetch() }}
        />
      }
      filters={
        nothingYet ? undefined : (
        <FilterLine
          filters={defs}
          state={filters}
          onChange={setFilters}
          searchLabel="Search bills to review"
          resultCount={read.data ? { shown: shown.length, total: read.data.total, noun: "bills" } : undefined}
          actions={
            <>
              {verdict ? (
                <span
                  className={styles.verdict}
                  data-testid="resume-verdict"
                  data-verdict={verdict}
                  data-outstanding={String(intake.data?.outstanding ?? "")}
                >
                  {/* Read, not inferred from the length of a list: `nothing_uploaded` and
                      `everything_decided` both have nothing waiting and only one is good news. A
                      verdict this build has no word for says so and claims nothing. */}
                  <StatusChip tone={verdict === "work_outstanding" ? "warn" : verdict === "everything_decided" ? "ok" : "neutral"} size="sm">
                    {verdict === "nothing_uploaded"
                      ? "Nothing uploaded"
                      : verdict === "everything_decided"
                        ? "All decided"
                        : verdict === "work_outstanding"
                          ? `${String(intake.data?.outstanding ?? 0)} waiting`
                          : "Unrecognised answer"}
                  </StatusChip>
                </span>
              ) : null}
              <InfoPopover infoKey="processing.expected-place" />
            </>
          }
        />
        )
      }
      table={
        read.error ? (
          <ReadFailed of="The review queue" error={read.error} onRetry={() => void read.refetch()} />
        ) : (
          <GridTable
            caption="Bills waiting for a person, oldest first"
            testId="to-review"
            columns={columns}
            rows={shown}
            rowKey={(document) => document.id}
            rowLabel={(document) => document.original_filename ?? "document"}
            onRowOpen={open}
            loading={read.isPending}
            empty={rows.length > 0 ? <EmptyState compact title="No bill matches" testId="to-review-no-match" /> : empty}
          />
        )
      }
    />
  );
}
